
// Services
import MessageService from '~/services/MessageService';
import { CandidateStudyProgram } from '~/services/CSPService';

// Components
import JobPreferences from '~/components/profile/cards/JobPreferences.vue';
import Languages from '~/components/profile/cards/Languages.vue';
import StudyPrograms from '~/components/profile/cards/StudyPrograms.vue';
import Skills from '~/components/profile/cards/Skills.vue';
import PersonalDetailsConfirm from '~/components/profile/cards/PersonalDetailsConfirm.vue';

// Page Components
import MissingFields from '../components/MissingFields.vue';

// Utils
import {
    IMissingFieldValidation,
    IProfileData,
    loadUserProfileData,
} from '~/utils/user/validateCandidateData';

import Vue, { PropType } from 'vue';
import {
    CandidateProfileActionId,
    CandidateProfileScorer,
    CandidateProfileStatus,
    ProfileRequirementType,
} from '~/utils/candidate/CandidateProfileScorer';

export default Vue.extend({
    name: 'MinimalProfileForm',
    components: {
        PersonalDetailsConfirm,
        Languages,
        StudyPrograms,
        Skills,
        MissingFields,
        JobPreferences,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: () => true,
            required: true,
        },
        profileRequirementType: {
            type: String as PropType<ProfileRequirementType>,
            required: true,
        },
    },
    data() {
        return {
            initialValidation: {} as IMissingFieldValidation,
            profileData: {} as IProfileData,
            show: false,

            shouldShowTerms: false,
        };
    },
    computed: {
        user: {
            get() {
                return this.$store.getters['profileModule/getUser']();
            },
            set(value) {
                this.$store.commit('profileModule/setUser', value);
            },
        },
        candidate: {
            get() {
                return this.$store.getters['profileModule/getCandidate']();
            },
            set(value) {
                this.$store.commit('profileModule/setCandidate', value);
            },
        },
        workAuthorizations: {
            get() {
                return this.$store.getters['profileModule/getWorkAuthorizations']();
            },
            set(value) {
                this.$store.commit('profileModule/setWorkAuthorizations', value);
            },
        },
        studyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getSortedStudyPrograms']();
        },
        missingFieldValidator(): IMissingFieldValidation {
            return this.missingFieldValidatorFromProfileScore();
        },
    },
    async mounted() {
        await this.load(false);

        this.shouldShowTerms = !this.profileData.terms;

        // Save initial visibility state of the components
        this.initialValidation = this.missingFieldValidator;

        console.debug('[mounted] add candidate:profile:update EventListener');

        window.addEventListener('candidate:profile:update', this.refreshAfterDataUpdatedAsync);
    },
    methods: {
        candidateProfileScore() {
            var scorer = new CandidateProfileScorer(
                this.candidate,
                this.user,
                this.workAuthorizations
            );
            return scorer.calculateScore(this.profileRequirementType);
        },
        async refreshAfterDataUpdatedAsync() {
            console.debug('refreshAfterDataUpdatedAsync');
            await this.load(true);

            this.refreshValidation();
        },
        refreshValidation() {
            var valid = this.checkIsUserProfilValid();
            this.$emit('validate:change', valid);
        },
        async load(silent: boolean): Promise<void> {
            if (!silent) this.$emit('isLoading:change', true);

            this.profileData = await loadUserProfileData();

            this.user = this.profileData.identityData;
            this.candidate = this.profileData.candidateData;
            this.workAuthorizations = this.profileData.workAuthorization;

            if (!silent) this.$emit('isLoading:change', false);
        },
        createOnSaveMessage(): void {
            MessageService.newMessage(
                (this.$t('general.success') as string).concat('!'),
                this.$t('spotlight.saveSuccessful')
            );
        },
        checkIsUserProfilValid(): boolean {
            var score = this.candidateProfileScore();
            return this.profileData.terms && score.status === CandidateProfileStatus.COMPLETE;
        },
        missingFieldValidatorFromProfileScore() {
            var score = this.candidateProfileScore();
            var actions = score.actions;

            const validation: IMissingFieldValidation = {
                email: true,
                firstName:
                    actions.filter(x => x.id == CandidateProfileActionId.FIRST_NAME).length < 1,
                lastName:
                    actions.filter(x => x.id == CandidateProfileActionId.LAST_NAME).length < 1,
                pronoun: actions.filter(x => x.id == CandidateProfileActionId.PRONOUN).length < 1,
                nationality:
                    actions.filter(x => x.id == CandidateProfileActionId.NATIONALITY).length < 1,
                country: actions.filter(x => x.id == CandidateProfileActionId.LOCATION).length < 1,
                gender: actions.filter(x => x.id == CandidateProfileActionId.GENDER).length < 1,
                workAuthorization:
                    actions.filter(x => x.id == CandidateProfileActionId.WORK_AUTHORIZATION)
                        .length < 1,
                disability:
                    actions.filter(x => x.id == CandidateProfileActionId.DISABILITY).length < 1,
                ethnicMinority: true,
                languages:
                    actions.filter(x => x.id == CandidateProfileActionId.LANGUAGES).length < 1,
                candidateStudyPrograms:
                    actions.filter(x => x.id == CandidateProfileActionId.STUDY_PROGRAMS).length < 1,
                skills: actions.filter(x => x.id == CandidateProfileActionId.SKILLS).length < 1,
                preferences:
                    actions.filter(x => x.id == CandidateProfileActionId.PREFERENCES).length < 1,
                terms: this.profileData.terms,
            };

            return validation;
        },
        personalDetailsHasEmptyRequiredFields(validation: IMissingFieldValidation): boolean {
            return (
                !validation.email ||
                !validation.firstName ||
                !validation.gender ||
                !validation.nationality ||
                !validation.country ||
                !validation.workAuthorization ||
                !validation.preferences
            );
        },
    },
    beforeDestroy() {
        console.debug('[beforeDestroy] remove candidate:profile:update EventListener');
        window.removeEventListener('candidate:profile:update', this.refreshAfterDataUpdatedAsync);
    },
    watch: {
        isLoading: {
            handler: function (value) {
                if (!value) {
                    this.$nextTick(() => {
                        this.show = true;
                    });
                } else {
                    this.show = false;
                }
            },
            immediate: true,
        },
        ['profileData.terms']: {
            handler: function () {
                this.refreshValidation();
            },
            immediate: true,
        },
    },
});
